import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import LoadingSpinner from '@/components/layout/LoadingSpinner'
import { Coffee, Star, Crown, Heart, Check } from 'lucide-react'
import VisitStampHistory from '@/components/customer/VisitStampHistory'

interface CustomerData {
  id: string
  visit_stamps: number
  referral_stamps: number
  points: number
  tenant_id: string
}

interface Reward {
  id: string
  tenant_id: string
  name: string
  description: string
  stamps_required: number
  reward_type: 'discount' | 'free_item' | 'points' | 'special'
  reward_value: string
  is_active: boolean
  created_at: string
  updated_at: string
  usage_count: number
}

interface RedemptionResult {
  success?: boolean
  error?: string
  redemption_code?: string
  reward_name?: string
  stamps_used?: number
}

const getIcon = (rewardType: string) => {
  switch (rewardType) {
    case 'discount': return Star
    case 'free_item': return Coffee
    case 'points': return Crown
    case 'special': return Heart
    default: return Coffee
  }
}

const CustomerVisitRewards = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [customerData, setCustomerData] = useState<CustomerData | null>(null)
  const [rewards, setRewards] = useState<Reward[]>([])
  const [loading, setLoading] = useState(true)
  const [redeeming, setRedeeming] = useState<string | null>(null)

  useEffect(() => {
    if (user) {
      fetchCustomerData()
    }
  }, [user])

  useEffect(() => {
    if (customerData) {
      fetchRewards()
    }
  }, [customerData])

  const fetchCustomerData = async () => {
    if (!user?.email) return

    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, visit_stamps, referral_stamps, points, tenant_id')
        .eq('email', user.email)
        .maybeSingle()

      if (error) throw error
      if (data) setCustomerData(data)
    } catch (error) {
      console.error('Error fetching customer data:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Kundendaten konnten nicht geladen werden.",
      })
    }
  }

  const fetchRewards = async () => {
    if (!customerData?.tenant_id) return

    try {
      const { data, error } = await supabase
        .from('rewards')
        .select('*')
        .eq('tenant_id', customerData.tenant_id)
        .eq('is_active', true)
        .order('stamps_required', { ascending: true })

      if (error) throw error
      setRewards((data as Reward[]) || [])
    } catch (error) {
      console.error('Error fetching rewards:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Besucherprämien konnten nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRedeem = async (reward: Reward) => {
    if (!customerData) return

    const availableStamps = customerData.visit_stamps
    
    if (availableStamps < reward.stamps_required) {
      toast({
        variant: "destructive",
        title: "Nicht genügend Stempel",
        description: `Sie benötigen ${reward.stamps_required} Besuchsstempel für diese Belohnung.`,
      })
      return
    }

    setRedeeming(reward.id)

    try {
      // Call the redeem_reward database function
      const { data, error } = await supabase.rpc('redeem_reward', {
        p_customer_id: customerData.id,
        p_reward_id: reward.id
      })

      if (error) throw error

      const result = data as RedemptionResult

      if (result?.error) {
        throw new Error(result.error)
      }

      if (result?.success) {
        toast({
          title: "Belohnung eingelöst!",
          description: `${result.reward_name} wurde erfolgreich eingelöst. Code: ${result.redemption_code}`,
        })

        // Refresh customer data
        await fetchCustomerData()
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Fehler beim Einlösen",
        description: "Die Belohnung konnte nicht eingelöst werden. Versuchen Sie es später erneut.",
      })
    } finally {
      setRedeeming(null)
    }
  }

  const canRedeem = (reward: Reward) => {
    if (!customerData) return false
    return customerData.visit_stamps >= reward.stamps_required
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <LoadingSpinner />
        </div>
      </DashboardLayout>
    )
  }

  const sortedRewards = rewards.sort((a, b) => a.stamps_required - b.stamps_required)
  const currentStamps = customerData?.visit_stamps || 0
  
  const getNextReward = () => {
    return sortedRewards.find(reward => currentStamps < reward.stamps_required)
  }
  
  const nextReward = getNextReward()

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Hallo {user?.email?.split('@')[0]},</h1>
          <h2 className="text-xl text-muted-foreground mb-4">Ihre Besucherprämien</h2>
          <p className="text-muted-foreground">
            Sammeln Sie Stempel bei jedem Besuch und lösen Sie tolle Prämien ein!
          </p>
        </div>

        {/* Rewards Progress Card */}
        {customerData && rewards.length > 0 && (
          <Card className="mb-8 border-2 border-primary/20 bg-gradient-to-r from-primary/5 to-background">
            <CardHeader>
              <CardTitle className="text-center text-primary mb-4">
                Sammeln Sie Stempel und sichern Sie sich tolle Belohnungen
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {/* Progress Bar */}
              <div className="relative mb-8">
                {/* Background line */}
                <div className="absolute top-6 left-0 right-0 h-1 bg-muted rounded-full"></div>
                
                {/* Progress line */}
                <div 
                  className="absolute top-6 left-0 h-1 bg-primary rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.min((currentStamps / (sortedRewards[sortedRewards.length - 1]?.stamps_required || 1)) * 100, 100)}%`
                  }}
                ></div>
                
                {/* Reward milestones */}
                <div className="flex justify-between items-start">
                  {sortedRewards.map((reward, index) => {
                    const isReached = currentStamps >= reward.stamps_required
                    const IconComponent = getIcon(reward.reward_type)
                    
                    return (
                      <div key={reward.id} className="flex flex-col items-center relative">
                        {/* Milestone circle */}
                        <div className={`w-12 h-12 rounded-full border-3 flex items-center justify-center mb-3 transition-all duration-300 ${
                          isReached 
                            ? 'bg-primary border-primary text-primary-foreground' 
                            : 'bg-background border-muted-foreground/30 text-muted-foreground'
                        }`}>
                          {isReached ? (
                            <Check className="h-6 w-6" />
                          ) : (
                            <span className="text-xs font-bold">{reward.stamps_required}</span>
                          )}
                        </div>
                        
                        {/* Reward image/icon placeholder */}
                        <div className={`w-16 h-16 rounded-lg border-2 flex items-center justify-center mb-2 transition-all duration-300 ${
                          isReached ? 'border-primary bg-primary/10' : 'border-muted bg-muted/30'
                        }`}>
                          <IconComponent className={`h-8 w-8 ${isReached ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        
                        {/* Reward name */}
                        <div className="text-center min-h-[2.5rem] flex items-center">
                          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                            isReached ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}>
                            {reward.name}
                          </span>
                        </div>
                        
                        {/* Status button */}
                        <div className="mt-2">
                          {isReached ? (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => handleRedeem(reward)}
                              disabled={redeeming === reward.id}
                              className="bg-primary/10 text-primary hover:bg-primary/20 text-xs px-3 py-1"
                            >
                              {redeeming === reward.id ? "Wird eingelöst..." : "Einlösen"}
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
              
              {/* Current status */}
              <div className="text-center">
                {nextReward ? (
                  <p className="text-sm text-muted-foreground">
                    Nur noch <strong className="text-primary">{nextReward.stamps_required - currentStamps} Besuche</strong> bis "{nextReward.name}"
                  </p>
                ) : (
                  <p className="text-sm text-success font-medium">
                    Herzlichen Glückwunsch! Sie haben alle Prämien erreicht!
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Current Stats */}
        {customerData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="flex items-center p-6">
                <Coffee className="h-8 w-8 text-primary mr-3" />
                <div>
                  <p className="text-2xl font-bold">{customerData.visit_stamps}</p>
                  <p className="text-sm text-muted-foreground">Besuchsstempel</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center p-6">
                <Heart className="h-8 w-8 text-accent mr-3" />
                <div>
                  <p className="text-2xl font-bold">{customerData.referral_stamps}</p>
                  <p className="text-sm text-muted-foreground">Empfehlungsstempel</p>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="flex items-center p-6">
                <Star className="h-8 w-8 text-success mr-3" />
                <div>
                  <p className="text-2xl font-bold">{customerData.points}</p>
                  <p className="text-sm text-muted-foreground">Punkte</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* All Available Rewards Details */}
        {rewards.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Verfügbare Besucherprämien</CardTitle>
              <CardDescription>
                Alle Prämien, die Sie mit Ihren Besuchsstempeln einlösen können
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2">
                {sortedRewards.map((reward) => {
                  const IconComponent = getIcon(reward.reward_type)
                  const isRedeemable = canRedeem(reward)
                  
                  return (
                    <Card key={reward.id} className={`border-2 ${isRedeemable ? 'border-primary/50' : 'border-muted'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-lg ${isRedeemable ? 'bg-primary/10' : 'bg-muted/30'}`}>
                            <IconComponent className={`h-6 w-6 ${isRedeemable ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h3 className="font-medium">{reward.name}</h3>
                              <Badge variant="outline">{reward.stamps_required} Stempel</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {reward.description}
                            </p>
                            <p className="text-sm font-medium mb-3">
                              Wert: {reward.reward_value}
                            </p>
                            {isRedeemable ? (
                              <Button
                                size="sm"
                                onClick={() => handleRedeem(reward)}
                                disabled={redeeming === reward.id}
                                className="w-full"
                              >
                                {redeeming === reward.id ? "Wird eingelöst..." : "Jetzt einlösen"}
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                disabled
                                className="w-full"
                              >
                                Noch {reward.stamps_required - currentStamps} Stempel benötigt
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {rewards.length === 0 && (
          <Card>
            <CardContent className="text-center py-8">
              <Coffee className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Noch keine Besucherprämien verfügbar</h3>
              <p className="text-muted-foreground">
                Schauen Sie später wieder vorbei - neue Prämien werden bald hinzugefügt!
              </p>
            </CardContent>
          </Card>
        )}

        {/* Visit Stamp History */}
        {customerData && (
          <Card className="mt-8">
            <CardHeader>
              <CardTitle>Besuchshistorie</CardTitle>
              <CardDescription>
                Ihre letzten Besuchsstempel im Überblick
              </CardDescription>
            </CardHeader>
            <CardContent>
              <VisitStampHistory customerId={customerData.id} tenantId={customerData.tenant_id} />
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  )
}

export default CustomerVisitRewards